import { term } from '../terms';
import { focusWhenShown } from './ui-controls';
import './ui-tabbar';
import './ui-shell';

// Navigation for the Louder component library: the top bar and its section panels, the ⌘K
// palette, the pager, folders (with drag to reorder, after Bencho's reorder list), the jump
// nav's scroll spy and the collapsing side nav (Bencho collapsing sidebar).

/** Top bar: one section panel open at a time; once one is open, pointing at another switches. */
export function uiTopbar() {
    return {
        open: null,
        init() { this.$watch('open', (name) => name && this.$nextTick(() => this.place(name))); },
        /** The open panel's left edge goes under its trigger, kept inside the bar. */
        place(name) {
            const sel = CSS.escape(name);
            const panel = this.$root.querySelector(`[data-panel="${sel}"]`);
            const btn = this.$root.querySelector(`[data-menu="${sel}"]`);
            if (!panel || !btn) return;
            const left = btn.getBoundingClientRect().left - this.$root.getBoundingClientRect().left - 8;
            panel.style.left = `${Math.max(0, Math.min(left, this.$root.clientWidth - panel.offsetWidth))}px`;
        },
        toggle(name) { this.open = this.open === name ? null : name; },
        hover(name) { if (this.open && this.open !== name) this.open = name; },
        close(refocus = false) {
            const was = this.open;
            this.open = null;
            if (refocus && was) this.$root.querySelector(`[data-menu="${CSS.escape(was)}"]`)?.focus();
        },
    };
}

/**
 * ⌘K palette. Opens on ⌘K / Ctrl K anywhere, on `/` outside a field, or on a `ui-cmdk` event.
 * Typing filters the catalogue (title and subtitle), arrows move, Enter opens, Esc closes.
 * items: [{ type, title, subtitle?, image?, icon?, href? }]
 */
export function uiCommand(items = [], opts = {}) {
    return {
        items,
        open: !!opts.inline,
        inline: !!opts.inline,
        q: opts.query ?? '',
        at: 0,
        recent: [],
        get hits() {
            const q = this.q.trim().toLowerCase();
            const list = q ? this.items.filter((i) => `${i.title} ${i.subtitle ?? ''}`.toLowerCase().includes(q)) : this.items.slice(0, 3).concat(this.items.filter((i) => i.type !== term('Sounds')).slice(0, 3));
            return list.slice(0, 12);
        },
        get groups() {
            const out = [];
            this.hits.forEach((h, i) => {
                let g = out.find((x) => x.type === h.type);
                if (!g) out.push((g = { type: h.type, rows: [] }));
                g.rows.push({ ...h, i });
            });
            return out;
        },
        init() {
            if (this.inline) return;
            window.addEventListener('keydown', (e) => {
                const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable;
                if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); this.show(); }
            });
            window.addEventListener('ui-cmdk', () => this.show());
        },
        show() {
            this.open = true;
            this.at = 0;
            focusWhenShown(this.$root, () => this.$refs.q);
        },
        close() {
            if (this.inline) { this.q = ''; return; }
            this.open = false;
        },
        mark(text) {
            const q = this.q.trim();
            const esc = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
            if (!q) return esc(text);
            const i = text.toLowerCase().indexOf(q.toLowerCase());
            return i < 0 ? esc(text) : `${esc(text.slice(0, i))}<mark>${esc(text.slice(i, i + q.length))}</mark>${esc(text.slice(i + q.length))}`;
        },
        key(e) {
            const n = this.hits.length;
            if (e.key === 'ArrowDown') { e.preventDefault(); this.at = (this.at + 1) % Math.max(n, 1); this.reveal(); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); this.at = (this.at - 1 + n) % Math.max(n, 1); this.reveal(); }
            else if (e.key === 'Enter') { e.preventDefault(); this.pick(this.hits[this.at]); }
            else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); this.close(); }
        },
        reveal() { this.$nextTick(() => this.$root.querySelector('.ui-r.ui-on')?.scrollIntoView({ block: 'nearest' })); },
        pick(hit) {
            if (!hit) return;
            this.recent = [hit.title, ...this.recent.filter((t) => t !== hit.title)].slice(0, 3);
            this.$dispatch('ui-toast', { tone: 'info', title: `Opening ${hit.title}`, text: `${hit.type.replace(/s$/, '')} · demo, nothing navigates` });
            this.close();
        },
    };
}

/** Pager: Prev / Next and numbers with gaps; `current` is x-modelable, changes dispatch `ui-page`. */
export function uiPager(cfg = {}) {
    return {
        current: cfg.current ?? 1,
        get last() { return Math.max(1, Math.ceil((cfg.total ?? 0) / (cfg.per ?? 50))); },
        get from() { return (this.current - 1) * (cfg.per ?? 50) + 1; },
        get to() { return Math.min(cfg.total ?? 0, this.current * (cfg.per ?? 50)); },
        get pages() {
            const set = [...new Set([1, 2, 3, this.current - 1, this.current, this.current + 1, this.last])].filter((p) => p >= 1 && p <= this.last).sort((a, b) => a - b);
            const out = [];
            set.forEach((p, i) => { if (i && p - set[i - 1] > 1) out.push('…' + p); out.push(p); });
            return out;
        },
        go(p) {
            if (cfg.locked || typeof p !== 'number' || p < 1 || p > this.last || p === this.current) return;
            this.current = p;
            this.$dispatch('ui-page', { page: p });
        },
    };
}

/**
 * Folders: pick one, add one inline, drag one to reorder (the others slide out of its way).
 * folders: [{ name, count }]; `active` is x-modelable.
 */
export function uiFolders(cfg = {}) {
    return {
        folders: (cfg.folders ?? []).map((f, i) => ({ ...f, id: `f${i}` })),
        active: cfg.active ?? 'all',
        adding: false,
        name: '',
        drag: null,
        get total() { return this.folders.reduce((s, f) => s + (f.count ?? 0), 0); },
        add() {
            const n = this.name.trim();
            if (!n) { this.adding = false; return; }
            const id = `f${Date.now()}`;
            this.folders.push({ id, name: n, count: 0 });
            this.active = id;
            this.name = '';
            this.adding = false;
            this.$dispatch('ui-toast', { tone: 'ok', title: `Folder "${n}" created`, text: 'Drag it to reorder.' });
        },
        startAdd() { this.adding = true; this.$nextTick(() => requestAnimationFrame(() => this.$refs.name?.focus())); },
        down(e, f) {
            if (e.button !== 0) return;
            const el = e.currentTarget;
            const sx = e.clientX;
            const sy = e.clientY;
            const grab = sx - el.getBoundingClientRect().left;
            let moved = false;
            const move = (ev) => {
                if (!moved) {
                    if (Math.hypot(ev.clientX - sx, ev.clientY - sy) < 5) return;
                    moved = true;
                    this.drag = f.id;
                }
                const over = [...this.$root.querySelectorAll('[data-folder]')].filter((n) => n.dataset.folder !== f.id).find((n) => {
                    const r = n.getBoundingClientRect();
                    return ev.clientX > r.left && ev.clientX < r.right && Math.abs(ev.clientY - (r.top + r.height / 2)) < r.height;
                });
                if (over) this.reorder(f.id, over.dataset.folder);
                el.style.translate = '0 0';
                const natural = el.getBoundingClientRect();
                el.style.translate = `${ev.clientX - grab - natural.left}px ${ev.clientY - sy}px`;
            };
            const up = () => {
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', up);
                if (!moved) return;
                el.animate([{ translate: el.style.translate }, { translate: '0 0' }], { duration: 280, easing: 'cubic-bezier(.34,1.3,.64,1)' });
                el.style.translate = '';
                this.drag = null;
                el.dataset.dragged = '1';
                setTimeout(() => delete el.dataset.dragged, 0);
            };
            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', up);
        },
        reorder(from, to) {
            const i = this.folders.findIndex((f) => f.id === from);
            const j = this.folders.findIndex((f) => f.id === to);
            if (i < 0 || j < 0) return;
            const before = new Map([...this.$root.querySelectorAll('[data-folder]')].map((n) => [n.dataset.folder, n.getBoundingClientRect().left]));
            const [x] = this.folders.splice(i, 1);
            this.folders.splice(j, 0, x);
            this.$nextTick(() => {
                this.$root.querySelectorAll('[data-folder]').forEach((n) => {
                    if (n.dataset.folder === from) return;
                    const dx = (before.get(n.dataset.folder) ?? 0) - n.getBoundingClientRect().left;
                    if (!dx) return;
                    n.animate([{ translate: `${dx}px 0` }, { translate: '0 0' }], { duration: 260, easing: 'cubic-bezier(.34,1.3,.64,1)' });
                });
            });
        },
        pick(f, e) {
            if (e?.currentTarget?.dataset.dragged) return;
            this.active = f.id;
        },
        keyMove(e, f) {
            if (!e.altKey || !['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
            e.preventDefault();
            const i = this.folders.findIndex((x) => x.id === f.id);
            const j = i + (e.key === 'ArrowLeft' ? -1 : 1);
            if (j < 0 || j >= this.folders.length) return;
            this.reorder(f.id, this.folders[j].id);
            this.$nextTick(() => this.$root.querySelector(`[data-folder="${f.id}"]`)?.focus());
        },
    };
}

/** Jump nav with scroll spy inside a scrolling box (`root` selector) or the page. */
export function uiJumpNav(cfg = {}) {
    return {
        current: cfg.current ?? null,
        init() {
            const root = cfg.root ? document.querySelector(cfg.root) : null;
            const targets = (cfg.ids ?? []).map((id) => document.getElementById(id)).filter(Boolean);
            const io = new IntersectionObserver((entries) => {
                entries.filter((e) => e.isIntersecting).forEach((e) => { this.current = e.target.id; });
            }, { root, rootMargin: '0px 0px -70% 0px', threshold: 0 });
            targets.forEach((t) => io.observe(t));
        },
        go(id, e) {
            e.preventDefault();
            const el = document.getElementById(id);
            if (!el) return;
            const root = cfg.root ? document.querySelector(cfg.root) : null;
            if (root) root.scrollTo({ top: el.offsetTop - root.offsetTop - 8, behavior: 'smooth' });
            else el.scrollIntoView({ behavior: 'smooth' });
            this.current = id;
        },
    };
}
